/**
 * Athena Workspace — Presentation Base Components
 * Correção 3 Camada 3 + Camada 4 (tipografia e hierarquia)
 *
 * Todos os tamanhos de texto referenciam CSS vars definidas em presentation.css.
 * Para o futuro slider de fonte: alterar apenas --ws-base no :root.
 * Cards, botões, ícones e espaçamentos de layout permanecem estáveis.
 */

import React from 'react';

// ── Design tokens (cores do WorkspacePage + vars tipográficas) ───────────────
export const PT = {
  // Cores (espelham WorkspacePage C)
  card:     '#0c1220',
  base:     '#07111F',
  border:   '#1a2240',
  borderHi: 'rgba(255,255,255,0.13)',
  text:     '#e8eaf2',
  textSec:  'rgba(232,234,242,0.74)',
  textMut:  '#6b7590',
  accent:   '#3B82F6',
  accentD:  '#1e3a5f',
  purple:   '#8b5cf6',
  gold:     '#f59e0b',
  green:    '#10b981',

  // Tipografia via CSS vars (slider altera --ws-base; o resto escala)
  fBase: 'var(--ws-text, 15px)',
  fSm:   'var(--ws-text-sm, 12.5px)',
  fXs:   'var(--ws-text-xs, 11px)',
  fLg:   'var(--ws-text-lg, 17px)',
  fXl:   'var(--ws-text-xl, 19px)',

  // Alturas de linha
  lineBody:    'var(--ws-line-body, 1.75)',
  lineTight:   'var(--ws-line-tight, 1.42)',
  lineHeading: 'var(--ws-line-heading, 1.28)',

  // Espaçamentos (fixos — não escalam com a fonte)
  cardPadV: 'var(--ws-card-pad-v, 17px)',
  cardPadH: 'var(--ws-card-pad-h, 20px)',
  spSection:'var(--ws-space-section, 20px)',
  spBlock:  'var(--ws-space-block, 13px)',
};

// ── 1. PresentationCard ──────────────────────────────────────────────────────
//    Card base reutilizável. Hierarquia: título → subtítulo → conteúdo → ações.
export function PresentationCard({
  title, subtitle, icon, children, variant = 'default', actions, style = {},
}) {
  const accentBar =
    variant === 'spiritual' ? PT.purple :
    variant === 'gold'      ? PT.gold   :
    variant === 'success'   ? PT.green  :
    PT.accent;

  return (
    <div style={{
      background: PT.card,
      border: `1px solid ${PT.border}`,
      borderRadius: 14,
      overflow: 'hidden',
      boxShadow: '0 2px 16px rgba(0,0,0,0.28)',
      marginBottom: 4,
      ...style,
    }}>
      {/* Cabeçalho — título + acento lateral */}
      {(title || icon) && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: `${PT.cardPadV} ${PT.cardPadH} 12px`,
          borderBottom: `1px solid rgba(255,255,255,0.055)`,
        }}>
          {icon && (
            <span style={{ fontSize: 20, lineHeight: 1, flexShrink: 0 }}>{icon}</span>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            {title && (
              <div style={{
                fontSize: PT.fXl,
                fontWeight: 700,
                color: PT.text,
                lineHeight: PT.lineHeading,
                letterSpacing: '-0.01em',
              }}>
                {title}
              </div>
            )}
            {subtitle && (
              <div style={{
                fontSize: PT.fSm,
                color: PT.textMut,
                marginTop: 4,
                lineHeight: PT.lineTight,
              }}>
                {subtitle}
              </div>
            )}
          </div>
          {/* Acento colorido — identidade do tipo de layout */}
          <div style={{
            width: 3, height: 22, borderRadius: 2,
            background: accentBar, flexShrink: 0, opacity: 0.9,
          }} />
        </div>
      )}

      {/* Conteúdo principal */}
      <div style={{ padding: `14px ${PT.cardPadH}` }}>
        {children}
      </div>

      {/* Ações */}
      {actions && (
        <div style={{ padding: `0 ${PT.cardPadH} 15px` }}>
          {actions}
        </div>
      )}
    </div>
  );
}

// ── 2. PresentationSection ───────────────────────────────────────────────────
//    Bloco de seção. Título em all-caps discreto; conteúdo com respiro adequado.
export function PresentationSection({ title, icon, content, children, accent }) {
  return (
    <div style={{ marginBottom: PT.spSection }}>
      {title && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 7,
          marginBottom: 10, paddingBottom: 7,
          borderBottom: `1px solid rgba(255,255,255,0.055)`,
        }}>
          {icon && <span style={{ fontSize: 14, flexShrink: 0 }}>{icon}</span>}
          <span style={{
            fontSize: PT.fXs,
            fontWeight: 700,
            color: accent || PT.textMut,
            textTransform: 'uppercase',
            letterSpacing: '0.09em',
          }}>
            {title}
          </span>
        </div>
      )}
      {content && (
        <div style={{
          fontSize: PT.fBase,
          color: PT.textSec,
          lineHeight: PT.lineBody,
          whiteSpace: 'pre-wrap',
        }}>
          {content}
        </div>
      )}
      {children}
    </div>
  );
}

// ── 3. HighlightBox ──────────────────────────────────────────────────────────
//    Callout visual para dicas, atenções, reflexões, versículos-chave.
const HB_STYLES = {
  info:      { border: '#3B82F6', bg: 'rgba(59,130,246,0.09)',  sym: '·', symColor: '#3B82F6' },
  warning:   { border: '#f59e0b', bg: 'rgba(245,158,11,0.09)',  sym: '△', symColor: '#f59e0b' },
  tip:       { border: '#10b981', bg: 'rgba(16,185,129,0.09)',  sym: '◆', symColor: '#10b981' },
  success:   { border: '#10b981', bg: 'rgba(16,185,129,0.10)',  sym: '✓', symColor: '#10b981' },
  spiritual: { border: '#8b5cf6', bg: 'rgba(139,92,246,0.11)', sym: '✦', symColor: '#8b5cf6' },
  note:      { border: '#4a5070', bg: 'rgba(74,80,112,0.16)',   sym: '◈', symColor: '#6b7590' },
};

export function HighlightBox({ type = 'info', title, content, children }) {
  const s = HB_STYLES[type] || HB_STYLES.info;
  return (
    <div style={{
      background: s.bg,
      border: `1px solid ${s.border}`,
      borderLeft: `3px solid ${s.border}`,
      borderRadius: 9,
      padding: '12px 16px',
      marginBottom: PT.spSection,
    }}>
      {title && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, marginBottom: 7,
        }}>
          <span style={{ fontSize: 13, color: s.symColor, flexShrink: 0 }}>{s.sym}</span>
          <span style={{
            fontSize: PT.fSm,
            fontWeight: 700,
            color: PT.text,
            lineHeight: PT.lineTight,
          }}>
            {title}
          </span>
        </div>
      )}
      {content && (
        <div style={{
          fontSize: PT.fBase,
          color: PT.textSec,
          lineHeight: PT.lineBody,
          whiteSpace: 'pre-wrap',
        }}>
          {content}
        </div>
      )}
      {children}
    </div>
  );
}

// ── 4. KeyValueGrid ──────────────────────────────────────────────────────────
//    Grade de informações. auto-fit + minmax garante largura confortável —
//    nunca força N colunas pela quantidade de itens; colapsa para 1 coluna
//    sozinho em containers estreitos (mobile) via CSS, sem breakpoint em JS.
export function KeyValueGrid({ items, columns = 'auto-fit' }) {
  if (!items || !items.length) return null;
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: `repeat(${columns}, minmax(240px, 1fr))`,
      gap: 8,
      marginBottom: PT.spSection,
    }}>
      {items.map(({ key, value }, i) => (
        <div key={i} style={{
          background: 'rgba(255,255,255,0.032)',
          border: `1px solid rgba(255,255,255,0.065)`,
          borderRadius: 9,
          padding: '9px 12px',
        }}>
          {/* Rótulo — discreto, não cresce com slider */}
          <div style={{
            fontSize: 10,
            fontWeight: 700,
            color: PT.textMut,
            textTransform: 'uppercase',
            letterSpacing: '0.07em',
            marginBottom: 4,
          }}>
            {key}
          </div>
          {/* Valor — escala com a fonte */}
          <div style={{
            fontSize: PT.fBase,
            fontWeight: 600,
            color: PT.text,
            lineHeight: PT.lineTight,
          }}>
            {value}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── 5. StepList ──────────────────────────────────────────────────────────────
//    Passos numerados. Números são badges fixos; texto escala.
export function StepList({ steps }) {
  if (!steps || !steps.length) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {steps.map((step, i) => (
        <div key={i} style={{
          display: 'flex', alignItems: 'flex-start', gap: 12,
          padding: '10px 14px',
          background: 'rgba(255,255,255,0.027)',
          border: `1px solid rgba(255,255,255,0.055)`,
          borderRadius: 9,
        }}>
          {/* Badge de número — tamanho fixo (não cresce com slider) */}
          <div style={{
            width: 22, height: 22, borderRadius: '50%',
            background: 'rgba(59,130,246,0.18)',
            border: '1px solid rgba(59,130,246,0.38)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 10, fontWeight: 800, color: PT.accent,
            flexShrink: 0, marginTop: 2,
          }}>
            {i + 1}
          </div>
          {/* Texto do passo — escala com a fonte */}
          <div style={{
            fontSize: PT.fBase,
            color: PT.textSec,
            lineHeight: PT.lineBody,
            flex: 1,
          }}>
            {step}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── 6. ComparisonBlock ───────────────────────────────────────────────────────
//    Colunas de comparação. Grid fixo; texto escala.
export function ComparisonBlock({ items, recommendation }) {
  if (!items || !items.length) return null;
  const cols = Math.min(items.length, 3);
  return (
    <div>
      <div style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${cols}, 1fr)`,
        gap: 10,
        marginBottom: 14,
      }}>
        {items.map((item, i) => (
          <div key={i} style={{
            background: 'rgba(255,255,255,0.036)',
            border: `1px solid ${PT.border}`,
            borderRadius: 10,
            padding: '13px 14px',
          }}>
            {/* Nome do item — hierarquia alta */}
            <div style={{
              fontSize: PT.fBase,
              fontWeight: 700,
              color: PT.text,
              marginBottom: 10,
              paddingBottom: 8,
              borderBottom: `1px solid rgba(255,255,255,0.055)`,
              lineHeight: PT.lineTight,
            }}>
              {item.name}
            </div>
            {item.pros?.map((p, j) => (
              <div key={j} style={{
                display: 'flex', gap: 7, marginBottom: 5,
                fontSize: PT.fSm, color: PT.textSec, lineHeight: PT.lineTight,
              }}>
                <span style={{ color: PT.green, fontWeight: 700, flexShrink: 0, marginTop: 1 }}>+</span>
                <span>{p}</span>
              </div>
            ))}
            {item.cons?.map((c, j) => (
              <div key={j} style={{
                display: 'flex', gap: 7, marginBottom: 5,
                fontSize: PT.fSm, color: PT.textSec, lineHeight: PT.lineTight,
              }}>
                <span style={{ color: PT.textMut, flexShrink: 0, marginTop: 1 }}>−</span>
                <span>{c}</span>
              </div>
            ))}
          </div>
        ))}
      </div>
      {recommendation && (
        <HighlightBox type="tip" title="Recomendação" content={recommendation} />
      )}
    </div>
  );
}

// ── 7. NarrativeBlock ────────────────────────────────────────────────────────
//    Texto narrativo fora do card — introdução ou comentário pós-card.
export function NarrativeBlock({ text, style = {} }) {
  if (!text) return null;
  return (
    <div style={{
      fontSize: PT.fBase,
      color: PT.textSec,
      lineHeight: PT.lineBody,
      marginBottom: 12,
      whiteSpace: 'pre-wrap',
      ...style,
    }}>
      {text}
    </div>
  );
}

// ── 8. FollowUpText ───────────────────────────────────────────────────────────
//    Pergunta de continuidade abaixo do card.
export function FollowUpText({ text }) {
  if (!text) return null;
  return (
    <div style={{
      fontSize: PT.fBase,
      color: PT.textMut,
      lineHeight: PT.lineBody,
      marginTop: 4,
      fontStyle: 'italic',
    }}>
      {text}
    </div>
  );
}

// ── 9. ActionBar ─────────────────────────────────────────────────────────────
//    Botões de ação. Tamanho estável — não cresce com slider de fonte.
const A_SYMS = {
  save: '○', copy: '⧉', share: '↗', expand: '⊞',
  continue: '→', plan: '◈', detail: '◎', adapt: '◇',
  study: '◆', download: '↓',
};

export function ActionBar({ actions = [], onAction }) {
  if (!actions.length) return null;
  return (
    <div style={{
      display: 'flex', flexWrap: 'wrap', gap: 6,
      paddingTop: 11,
      borderTop: `1px solid rgba(255,255,255,0.055)`,
    }}>
      {actions.map((a, i) => (
        <ActionBtn key={i} id={a.id} label={a.label} onAction={onAction} />
      ))}
    </div>
  );
}

function ActionBtn({ id, label, onAction }) {
  const [hov, setHov] = React.useState(false);
  return (
    <button
      onClick={() => onAction && onAction(id || label)}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 5,
        padding: '5px 12px',
        // Tamanho do botão é FIXO — não usa var(--ws-text)
        fontSize: 12,
        background: hov ? 'rgba(59,130,246,0.14)' : 'rgba(255,255,255,0.042)',
        border: `1px solid ${hov ? 'rgba(59,130,246,0.38)' : 'rgba(255,255,255,0.09)'}`,
        borderRadius: 20,
        color: hov ? PT.text : PT.textMut,
        cursor: 'pointer',
        transition: 'all 0.14s ease',
        lineHeight: 1,
      }}
    >
      <span style={{ fontSize: 11 }}>{A_SYMS[id] || '◦'}</span>
      <span>{label}</span>
    </button>
  );
}
